/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `device_info` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class DeviceInfoMesg : Mesg {
    public constructor() : super("DeviceInfo", Profile.MesgNum.DEVICE_INFO)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** device_index. */
    public var deviceIndex: UByte?
        get() = getFieldValue(DEVICE_INDEX_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEVICE_INDEX_FIELD_NUM, value)

    /** device_type. */
    public var deviceType: UByte?
        get() = getFieldValue(DEVICE_TYPE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEVICE_TYPE_FIELD_NUM, value)

    /**
     * ble_device_type: what device_type means when this message selects it.
     */
    public val bleDeviceType: BleDeviceType?
        get() = (getFieldValue(DEVICE_TYPE_FIELD_NUM, 0, SubFieldSelector.Named("BleDeviceType")) as? UByte)?.let { BleDeviceType.fromValue(it) }

    public fun setBleDeviceType(value: BleDeviceType?) {
        setFieldValue(DEVICE_TYPE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("BleDeviceType"))
    }

    /**
     * antplus_device_type: what device_type means when this message selects it.
     */
    public val antplusDeviceType: AntplusDeviceType?
        get() = (getFieldValue(DEVICE_TYPE_FIELD_NUM, 0, SubFieldSelector.Named("AntplusDeviceType")) as? UByte)?.let { AntplusDeviceType.fromValue(it) }

    public fun setAntplusDeviceType(value: AntplusDeviceType?) {
        setFieldValue(DEVICE_TYPE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("AntplusDeviceType"))
    }

    /**
     * ant_device_type: what device_type means when this message selects it.
     */
    public val antDeviceType: UByte?
        get() = getFieldValue(DEVICE_TYPE_FIELD_NUM, 0, SubFieldSelector.Named("AntDeviceType")) as? UByte

    public fun setAntDeviceType(value: UByte?) {
        setFieldValue(DEVICE_TYPE_FIELD_NUM, value, 0, SubFieldSelector.Named("AntDeviceType"))
    }

    /**
     * local_device_type: what device_type means when this message selects it.
     */
    public val localDeviceType: LocalDeviceType?
        get() = (getFieldValue(DEVICE_TYPE_FIELD_NUM, 0, SubFieldSelector.Named("LocalDeviceType")) as? UByte)?.let { LocalDeviceType.fromValue(it) }

    public fun setLocalDeviceType(value: LocalDeviceType?) {
        setFieldValue(DEVICE_TYPE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("LocalDeviceType"))
    }

    /** manufacturer. */
    public var manufacturer: Manufacturer?
        get() = (getFieldValue(MANUFACTURER_FIELD_NUM) as? UShort)?.let { Manufacturer.fromValue(it) }
        set(value) = setFieldValue(MANUFACTURER_FIELD_NUM, value?.value)

    /** serial_number. */
    public var serialNumber: UInt?
        get() = getFieldValue(SERIAL_NUMBER_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SERIAL_NUMBER_FIELD_NUM, value)

    /** product. */
    public var product: UShort?
        get() = getFieldValue(PRODUCT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PRODUCT_FIELD_NUM, value)

    /**
     * favero_product: what product means when this message selects it.
     */
    public val faveroProduct: FaveroProduct?
        get() = (getFieldValue(PRODUCT_FIELD_NUM, 0, SubFieldSelector.Named("FaveroProduct")) as? UShort)?.let { FaveroProduct.fromValue(it) }

    public fun setFaveroProduct(value: FaveroProduct?) {
        setFieldValue(PRODUCT_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("FaveroProduct"))
    }

    /**
     * garmin_product: what product means when this message selects it.
     */
    public val garminProduct: GarminProduct?
        get() = (getFieldValue(PRODUCT_FIELD_NUM, 0, SubFieldSelector.Named("GarminProduct")) as? UShort)?.let { GarminProduct.fromValue(it) }

    public fun setGarminProduct(value: GarminProduct?) {
        setFieldValue(PRODUCT_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("GarminProduct"))
    }

    /** software_version. */
    public var softwareVersion: Double?
        get() = getFieldValue(SOFTWARE_VERSION_FIELD_NUM) as? Double
        set(value) = setFieldValue(SOFTWARE_VERSION_FIELD_NUM, value)

    /** hardware_version. */
    public var hardwareVersion: UByte?
        get() = getFieldValue(HARDWARE_VERSION_FIELD_NUM) as? UByte
        set(value) = setFieldValue(HARDWARE_VERSION_FIELD_NUM, value)

    /** cum_operating_time, in s. Reset by new battery or charge. */
    public var cumOperatingTime: UInt?
        get() = getFieldValue(CUM_OPERATING_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CUM_OPERATING_TIME_FIELD_NUM, value)

    /** battery_voltage, in V. */
    public var batteryVoltage: Double?
        get() = getFieldValue(BATTERY_VOLTAGE_FIELD_NUM) as? Double
        set(value) = setFieldValue(BATTERY_VOLTAGE_FIELD_NUM, value)

    /** battery_status. */
    public var batteryStatus: BatteryStatus?
        get() = (getFieldValue(BATTERY_STATUS_FIELD_NUM) as? UByte)?.let { BatteryStatus.fromValue(it) }
        set(value) = setFieldValue(BATTERY_STATUS_FIELD_NUM, value?.value)

    /** sensor_position. Indicates the location of the sensor */
    public var sensorPosition: BodyLocation?
        get() = (getFieldValue(SENSOR_POSITION_FIELD_NUM) as? UByte)?.let { BodyLocation.fromValue(it) }
        set(value) = setFieldValue(SENSOR_POSITION_FIELD_NUM, value?.value)

    /** descriptor. Used to describe the sensor or location */
    public var descriptor: String?
        get() = getFieldValue(DESCRIPTOR_FIELD_NUM) as? String
        set(value) = setFieldValue(DESCRIPTOR_FIELD_NUM, value)

    /** ant_transmission_type. */
    public var antTransmissionType: UByte?
        get() = getFieldValue(ANT_TRANSMISSION_TYPE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(ANT_TRANSMISSION_TYPE_FIELD_NUM, value)

    /** ant_device_number. */
    public var antDeviceNumber: UShort?
        get() = getFieldValue(ANT_DEVICE_NUMBER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(ANT_DEVICE_NUMBER_FIELD_NUM, value)

    /** ant_network. */
    public var antNetwork: AntNetwork?
        get() = (getFieldValue(ANT_NETWORK_FIELD_NUM) as? UByte)?.let { AntNetwork.fromValue(it) }
        set(value) = setFieldValue(ANT_NETWORK_FIELD_NUM, value?.value)

    /** source_type. */
    public var sourceType: SourceType?
        get() = (getFieldValue(SOURCE_TYPE_FIELD_NUM) as? UByte)?.let { SourceType.fromValue(it) }
        set(value) = setFieldValue(SOURCE_TYPE_FIELD_NUM, value?.value)

    /** product_name. Optional free form string to indicate the devices name or model */
    public var productName: String?
        get() = getFieldValue(PRODUCT_NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(PRODUCT_NAME_FIELD_NUM, value)

    /** battery_level, in %. */
    public var batteryLevel: UByte?
        get() = getFieldValue(BATTERY_LEVEL_FIELD_NUM) as? UByte
        set(value) = setFieldValue(BATTERY_LEVEL_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val DEVICE_INDEX_FIELD_NUM: UByte = 0u
        public const val DEVICE_TYPE_FIELD_NUM: UByte = 1u
        public const val MANUFACTURER_FIELD_NUM: UByte = 2u
        public const val SERIAL_NUMBER_FIELD_NUM: UByte = 3u
        public const val PRODUCT_FIELD_NUM: UByte = 4u
        public const val SOFTWARE_VERSION_FIELD_NUM: UByte = 5u
        public const val HARDWARE_VERSION_FIELD_NUM: UByte = 6u
        public const val CUM_OPERATING_TIME_FIELD_NUM: UByte = 7u
        public const val BATTERY_VOLTAGE_FIELD_NUM: UByte = 10u
        public const val BATTERY_STATUS_FIELD_NUM: UByte = 11u
        public const val SENSOR_POSITION_FIELD_NUM: UByte = 18u
        public const val DESCRIPTOR_FIELD_NUM: UByte = 19u
        public const val ANT_TRANSMISSION_TYPE_FIELD_NUM: UByte = 20u
        public const val ANT_DEVICE_NUMBER_FIELD_NUM: UByte = 21u
        public const val ANT_NETWORK_FIELD_NUM: UByte = 22u
        public const val SOURCE_TYPE_FIELD_NUM: UByte = 25u
        public const val PRODUCT_NAME_FIELD_NUM: UByte = 27u
        public const val BATTERY_LEVEL_FIELD_NUM: UByte = 32u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("DeviceIndex", 0u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[1] = Field("DeviceType", 1u, BaseType.UINT8, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("BleDeviceType", BaseType.UINT8, 1.0, 0.0, "").also { subField ->
                        subField.addMap(25u, 3)
                    },
                )
                .addSubField(
                    SubField("AntplusDeviceType", BaseType.UINT8, 1.0, 0.0, "").also { subField ->
                        subField.addMap(25u, 1)
                    },
                )
                .addSubField(
                    SubField("AntDeviceType", BaseType.UINT8, 1.0, 0.0, "").also { subField ->
                        subField.addMap(25u, 0)
                    },
                )
                .addSubField(
                    SubField("LocalDeviceType", BaseType.UINT8, 1.0, 0.0, "").also { subField ->
                        subField.addMap(25u, 5)
                    },
                )
            fields[2] = Field("Manufacturer", 2u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[3] = Field("SerialNumber", 3u, BaseType.UINT32Z, 1.0, 0.0, "", false)
            fields[4] = Field("Product", 4u, BaseType.UINT16, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("FaveroProduct", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(2u, 263)
                    },
                )
                .addSubField(
                    SubField("GarminProduct", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(2u, 1)
                        subField.addMap(2u, 15)
                        subField.addMap(2u, 13)
                        subField.addMap(2u, 89)
                    },
                )
            fields[5] = Field("SoftwareVersion", 5u, BaseType.UINT16, 100.0, 0.0, "", false)
            fields[6] = Field("HardwareVersion", 6u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[7] = Field("CumOperatingTime", 7u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[10] = Field("BatteryVoltage", 10u, BaseType.UINT16, 256.0, 0.0, "V", false)
            fields[11] = Field("BatteryStatus", 11u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[18] = Field("SensorPosition", 18u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[19] = Field("Descriptor", 19u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[20] = Field("AntTransmissionType", 20u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[21] = Field("AntDeviceNumber", 21u, BaseType.UINT16Z, 1.0, 0.0, "", false)
            fields[22] = Field("AntNetwork", 22u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[25] = Field("SourceType", 25u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[27] = Field("ProductName", 27u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[32] = Field("BatteryLevel", 32u, BaseType.UINT8, 1.0, 0.0, "%", false)
            return fields
        }
    }
}
